import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import { useTrackViewedProduct } from 'utils/klaviyo';
import { useOnCheckoutClick } from 'utils/checkout-link';
import { buildImgProps } from 'utils/media';
import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import formatCurrency from 'utils/format-currency';
import { parsePrice, convertPrice } from 'utils/price';
import useLocalCurrency from 'utils/useLocalCurrency';
import { getAmountSaved, getOfferUnitPrice, getPercentSaved } from 'utils/price';
import { buildCssVar } from 'utils/style-override';

import RatingStars from 'generic/RatingStars';
import Spinner from 'generic/Spinner';
import AfterpayWidget from 'generic/AfterpayWidget';

import type {
  ComponentsMultiBundleStyle,
  ComponentsMultiBundleOffer,
  ComponentsAfterpayWidget,
} from 'queries/graphql';

import styles from './styles.module.scss';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

export default function MultiBundleItem({
  salesPageOffer,
  name,
  heading,
  image,
  ctaText,
  text,
  rating,
  showProductUnitPrice,
  style,
}: ComponentsMultiBundleOffer & {
  afterpayWidget: ComponentsAfterpayWidget;
  showProductUnitPrice: boolean;
  isAfterpaySupported: boolean;
  style: ComponentsMultiBundleStyle;
}) {
  const { offer } = salesPageOffer;
  const { currency, symbol, rate } = useLocalCurrency();

  const {
    headingColor,
    headingBackgroundColor,
    productNameColor,
    textColor,
    backgroundColor,
    compareAtColor,
    buttonTextColor,
    buttonBackgroundColor,
    buttonHoverColor,
    buttonHoverTextColor,
    buttonBorderColor,
    buttonHoverBorderColor,
    headingFont,
    textFont,
  } = style || {};

  const [shouldShowActivityIndicator, setShouldShowActivityIndicator] = useState(false);

  const getCheckoutClickHandler = useOnCheckoutClick({
    setShouldShowActivityIndicator,
    shouldTrackSelectItem: true,
    // @lkuich - Should we load anything here?
    paymentMethod: null,
  });

  useTrackViewedProduct(offer);

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  const { price, compareAtPrice } = useMemo(() => {
    if (showProductUnitPrice) {
      const unitPrice = `${formatCurrency(
        getOfferUnitPrice(convertPrice(offer?.price, rate), offer?.storeProductQuantity),
        false,
        symbol
      )} ${currency} each`;
      const compareAtUnitPrice = `${formatCurrency(
        getOfferUnitPrice(convertPrice(offer?.compareAtPrice, rate), offer?.storeProductQuantity),
        false,
        symbol
      )} ${currency} each`;

      return { price: unitPrice, compareAtPrice: compareAtUnitPrice };
    }

    return {
      price: `${formatCurrency(convertPrice(offer?.price, rate))} ${currency}`,
      compareAtPrice: `${formatCurrency(
        parsePrice(offer?.compareAtPrice),
        false,
        symbol
      )} ${currency}`,
    };
  }, [
    offer?.price,
    offer?.storeProductQuantity,
    showProductUnitPrice,
    currency,
    rate,
    symbol,
    offer?.compareAtPrice,
  ]);

  return (
    <div className={`root ${styles.container}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--heading-color', headingColor, 'var(--primary-color)')}
          ${buildCssVar(
            '--heading-background-color',
            headingBackgroundColor,
            'var(--secondary-color)'
          )}
          ${buildCssVar('--product-name-color', productNameColor, 'var(--text-color-dark)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
          ${buildCssVar('--compare-at-color', compareAtColor, 'var(--color-negative)')}
          ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
          ${buildCssVar('--button-background-color', buttonBackgroundColor, 'var(--primary-color)')}
          ${buildCssVar('--button-hover-color', buttonHoverColor, 'var(--primary-color)')}
          ${buildCssVar(
            '--button-hover-text-color',
            buttonHoverTextColor,
            'var(--text-color-light)'
          )}
          ${buildCssVar(
            '--button-border-color',
            buttonBorderColor,
            buttonBackgroundColor,
            'var(--primary-color)'
          )}
          ${buildCssVar(
            '--button-hover-border-color',
            buttonHoverBorderColor,
            buttonHoverColor,
            'var(--primary-color)'
          )}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <div>
        {shouldShowActivityIndicator && <Spinner />}
        {heading && (
          <div className={styles.bundle_banner}>
            <h3>{heading}</h3>
          </div>
        )}
        <div className={styles.product_detail_container}>
          <div className={styles.product_image_wrapper}>
            <Image {...buildImgProps(image)} alt={name || offer?.name} />
          </div>
          <div className={styles.bundle_name_items_wrapper}>
            <h2 className={styles.product_name}>{name || offer?.name}</h2>
            {!!rating && (
              <div>
                <RatingStars rating={rating} />
              </div>
            )}
            {text && (
              <div className={styles.bundle_detail_wrapper}>
                <MarkdownText text={text} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.price_button_wrapper}>
        {compareAtPrice && <p className={styles.compare_price}>{compareAtPrice}</p>}
        <h2 className={styles.price_wrapper}>{price}</h2>
        <button
          onClick={getCheckoutClickHandler([{ offerId: offer?.hashedId, quantity: 1 }])}
          className={styles.product_btn}
        >
          {ctaText}
        </button>
      </div>
    </div>
  );
}

export function MultiBundleItemTheme2({
  salesPageOffer,
  name,
  heading,
  image,
  ctaText,
  showSavingsPercent,
  afterpayWidget,
  isAfterpaySupported,
  showProductUnitPrice,
  style,
}: ComponentsMultiBundleOffer & {
  afterpayWidget: ComponentsAfterpayWidget;
  showProductUnitPrice: boolean;
  isAfterpaySupported: boolean;
  style: ComponentsMultiBundleStyle;
}) {
  const { currency, symbol, rate } = useLocalCurrency();

  const {
    headingColor,
    headingBackgroundColor,
    productNameColor,
    textColor,
    backgroundColor,
    compareAtColor,
    buttonTextColor,
    buttonBackgroundColor,
    buttonHoverColor,
    buttonHoverTextColor,
    buttonBorderColor,
    buttonHoverBorderColor,
    headingFont,
    textFont,
  } = style || {};

  const [shouldShowActivityIndicator, setShouldShowActivityIndicator] = useState(false);

  const getCheckoutClickHandler = useOnCheckoutClick({
    setShouldShowActivityIndicator,
    shouldTrackSelectItem: true,
    // @lkuich - Should we load anything here?
    paymentMethod: null,
  });

  const { offer } = salesPageOffer;

  const { price, compareAtPrice } = useMemo(() => {
    if (showProductUnitPrice) {
      const unitPrice = `${formatCurrency(
        getOfferUnitPrice(convertPrice(offer?.price, rate), offer?.storeProductQuantity),
        false,
        symbol
      )} ${currency} each`;
      const compareAtUnitPrice = `Retail ${formatCurrency(
        getOfferUnitPrice(convertPrice(offer?.compareAtPrice, rate), offer?.storeProductQuantity),
        false,
        symbol
      )} ${currency} each`;

      return { price: unitPrice, compareAtPrice: compareAtUnitPrice };
    }

    return {
      price: `${formatCurrency(convertPrice(offer?.price, rate))} ${currency}`,
      compareAtPrice: `Retail ${formatCurrency(
        parsePrice(offer?.compareAtPrice),
        false,
        symbol
      )} ${currency}`,
    };
  }, [
    offer?.price,
    offer?.storeProductQuantity,
    showProductUnitPrice,
    currency,
    rate,
    symbol,
    offer?.compareAtPrice,
  ]);

  useTrackViewedProduct(offer);

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  return (
    <div className={`root ${styles.container}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--heading-color', headingColor, 'var(--primary-color)')}
          ${buildCssVar(
            '--heading-background-color',
            headingBackgroundColor,
            'var(--background-color-grey)'
          )}
          ${buildCssVar('--product-name-color', productNameColor, 'var(--text-color-dark)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
          ${buildCssVar('--compare-at-color', compareAtColor, 'var(--color-negative)')}
          ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
          ${buildCssVar('--button-background-color', buttonBackgroundColor, 'var(--primary-color)')}
          ${buildCssVar('--button-hover-color', buttonHoverColor, 'var(--primary-color)')}
          ${buildCssVar(
            '--button-hover-text-color',
            buttonHoverTextColor,
            'var(--text-color-light)'
          )}
          ${buildCssVar(
            '--button-border-color',
            buttonBorderColor,
            buttonBackgroundColor,
            'var(--primary-color)'
          )}
          ${buildCssVar(
            '--button-hover-border-color',
            buttonHoverBorderColor,
            buttonHoverColor,
            'var(--primary-color)'
          )}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <div>
        {shouldShowActivityIndicator && <Spinner />}
        {heading && (
          <div className={styles.bundle_banner}>
            <h3>{heading}</h3>
          </div>
        )}
        <div className={styles.product_detail_container}>
          <div className={styles.product_image_wrapper}>
            <Image {...buildImgProps(image)} alt={name || offer?.name} />
          </div>
          <div className={styles.bundle_name_items_wrapper}>
            <p className={styles.product_name}>{name || offer?.name}</p>
            {offer?.compareAtPrice && <p className={styles.compare_price}>{compareAtPrice}</p>}
            {price && <p className={styles.price_wrapper}>{price}</p>}
            {offer?.compareAtPrice && (
              <p className={styles.savings}>
                (Save{' '}
                {showSavingsPercent
                  ? `${getPercentSaved(offer.compareAtPrice, offer?.price)}% - `
                  : ''}{' '}
                {formatCurrency(
                  convertPrice(getAmountSaved(offer.compareAtPrice, offer?.price), rate),
                  true
                )}{' '}
                {currency})
              </p>
            )}
            {isAfterpaySupported && afterpayWidget && (
              <AfterpayWidget
                price={convertPrice(offer?.price, rate)}
                currency={currency}
                {...afterpayWidget}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles.price_button_wrapper}>
        <button
          onClick={getCheckoutClickHandler([{ offerId: offer?.hashedId, quantity: 1 }])}
          className={styles.product_btn}
        >
          {ctaText}
        </button>
      </div>
    </div>
  );
}
